import {
  CSSProperties,
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import styled from "@emotion/styled";
import { useMediaQuery } from "@react-hooks-library/core";
import { Modal } from "@9amhealth/shared";
import { APP_BREAKPOINT } from "src/constants/layout";
import translate from "src/lib/translate";
import Translate from "src/ui/components/Translate/Translate";

import IconMinimise from "./IconMinimise";

const MODAL_STYLE = {
  "--dialog-background": "var(--color-white, #fff)",
  "--border-radius": "1.5rem",
  "--width": "100%",
  "--max-width": "700px",
  "--max-height": "100%",
  alignItems: "stretch",
  padding:
    "calc(var(--app-top, 64px) + 5.475rem) 1.5rem calc(var(--app-bottom, 55px) + 1.5rem)"
} as CSSProperties;

// On desktop the card is a fixed 672x566 panel that hangs 5rem below the top
// nav (`--app-top` = safe area + nav height) instead of stretching into the
// mobile insets. `--max-height` keeps it inside the viewport on short screens.
const DESKTOP_MODAL_STYLE = {
  "--dialog-background": "var(--color-white, #fff)",
  "--border-radius": "1.5rem",
  "--width": "672px",
  "--max-width": "672px",
  "--max-height": "100%",
  alignItems: "flex-start",
  padding: "calc(var(--app-top, 64px) + 5rem) 1.5rem 2rem"
} as CSSProperties;

// The `alert` variant has no `--height` custom prop — it hugs its content — so
// the fixed desktop height is applied through this instance's className only,
// leaving every other alert dialog in the app untouched.
const InsightModal = styled(Modal)`
  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    height: 566px;
  }
`;

const Panel = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 1rem 0 0;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 1rem;
  padding: 0 1rem 0.75rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    padding: 0;
  }
`;

const Label = styled.p`
  flex: 1 0 0;
  margin: 0;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;

const MinimiseButton = styled.button`
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--Greys-Gray, #aaa7a0);
  line-height: 0;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ScrollWrap = styled.div`
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  display: flex;
`;

const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1rem 2rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    padding: 0;
  }
`;

const Insight = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-size: 1.125rem;
  line-height: 1.5;
  letter-spacing: -0.18px;
  white-space: pre-line;
`;

const Footer = styled.p`
  margin: 1rem 0 0;
  color: var(--color-charcoal-60);
  font-size: 0.75rem;
  line-height: 1.33;
`;

const Fog = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 3.5rem;
  pointer-events: none;

  &[data-edge="top"] {
    top: 0;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 0),
      var(--color-white, #fff)
    );
  }

  &[data-edge="bottom"] {
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      var(--color-white, #fff)
    );
  }
`;

interface MealTodayInsightModalProps {
  insights: string;
  footer?: string;
  onClose: () => void;
}

const MealTodayInsightModal: FC<MealTodayInsightModalProps> = ({
  insights,
  footer,
  onClose
}) => {
  const isDesktop = useMediaQuery(`(min-width: ${APP_BREAKPOINT}px)`);
  const [isOpen, setIsOpen] = useState(true);
  const [showTopFog, setShowTopFog] = useState(false);
  const [showBottomFog, setShowBottomFog] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const measureFog = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 1;
    setShowTopFog(overflowing && el.scrollTop > 1);
    setShowBottomFog(
      overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 1
    );
  }, []);

  useLayoutEffect(() => {
    measureFog();
  }, [measureFog, insights, isDesktop]);

  return (
    <InsightModal
      isOpen={isOpen}
      variant="alert"
      style={isDesktop ? DESKTOP_MODAL_STYLE : MODAL_STYLE}
      ariaLabel={translate("mealInsights.today.insightsLabel")}
      onWillDismiss={() => setIsOpen(false)}
      onDidDismiss={onClose}
    >
      <Panel>
        <Header>
          <Label>
            <Translate msg="mealInsights.today.insightsLabel" />
          </Label>
          <MinimiseButton
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label={translate("mealInsights.today.collapse")}
          >
            <IconMinimise />
          </MinimiseButton>
        </Header>
        <ScrollWrap>
          <ScrollArea ref={scrollRef} onScroll={measureFog}>
            <Insight>{insights}</Insight>
            {footer && <Footer>{footer}</Footer>}
          </ScrollArea>
          {showTopFog && <Fog data-edge="top" aria-hidden />}
          {showBottomFog && <Fog data-edge="bottom" aria-hidden />}
        </ScrollWrap>
      </Panel>
    </InsightModal>
  );
};

export default MealTodayInsightModal;
